import { CompoundEntityRef } from '@backstage/catalog-model';
import { z } from 'zod/v3';

export const DOCUMENT_TYPES = [
  'catalog',
  'techdocs',
  'coda',
  'code-ownership',
  'tech-radar',
  'lingo',
  'explore-tool',
  'slack',
  'specification',
] as const;

const documentType = z.enum(
  DOCUMENT_TYPES as unknown as [
    (typeof DOCUMENT_TYPES)[number],
    ...Array<(typeof DOCUMENT_TYPES)[number]>,
  ],
);
type DocumentType = z.infer<typeof documentType>;

export const searchConfigSchema = z.object({
  filters: z
    .object({
      ignoredDomains: z.array(z.string()).optional(),
    })
    .optional(),
  urls: z
    .array(
      z.object({
        url: z.string(),
        owner: z.string(),
        tags: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

type FrontMatterValue =
  | string
  | number
  | boolean
  | null
  | FrontMatterValue[]
  | { [key: string]: FrontMatterValue };

export interface IndexDocument {
  format: 'text' | 'markdown' | 'html' | 'csv' | 'pdf' | 'docx' | 'xlsx';
  type: DocumentType;
  title: string;
  text: string;
  location: string;
  path: string;
  kind: string;
  name: string;
  namespace: string;
  domain?: string;
  domainRef?: CompoundEntityRef;
  domains?: string[];
  lifecycle?: string;
  owner?: string;
  ownerRef?: CompoundEntityRef;
  owners?: string[];
  tags?: string[];
  keywords?: string[];
  summary?: string;
  displayTitle?: string;
  repo?: string;
  repoSlug?: string;
  custom?: Record<string, FrontMatterValue>;
  lastModified?: Date;
}

const compoundEntityRefSchema = z.object({
  kind: z.string(),
  name: z.string(),
  namespace: z.string(),
});

export const semanticSearchFiltersSchema = z.array(
  z.union([
    z.object({
      key: z.string(),
      value: z.boolean(),
      condition: z.enum(['equals', 'not equals']),
    }),
    z.object({
      key: z.string(),
      value: z.string(),
      condition: z.enum(['equals', 'not equals', 'contains']),
    }),
    z.object({
      key: z.string(),
      value: z.number(),
      condition: z.enum([
        'equals',
        'not equals',
        'greater than',
        'greater than or equals',
        'less than',
        'less than or equals',
      ]),
    }),
    z.object({
      key: z.string(),
      value: z.array(z.string()).min(1),
      condition: z.enum(['in', 'not in']),
    }),
  ]),
);
export type SemanticSearchFilters = z.infer<typeof semanticSearchFiltersSchema>;

export const semanticSearchRetrieveQuerySchema = z.object({
  text: z.string(),
  filters: semanticSearchFiltersSchema.optional(),
});
export type SemanticSearchRetrieveQuery = z.infer<
  typeof semanticSearchRetrieveQuerySchema
>;

export const semanticSearchRetrieveAndGenerateQuerySchema = z.object({
  text: z.string(),
  sessionId: z.string().optional(),
  filters: semanticSearchFiltersSchema.optional(),
});
export type SemanticSearchRetrieveAndGenerateQuery = z.infer<
  typeof semanticSearchRetrieveAndGenerateQuerySchema
>;

const semanticSearchChunkSchema = compoundEntityRefSchema.extend({
  // required
  type: documentType,
  title: z.string(),
  location: z.string().url(),
  path: z.string(),
  text: z.string(),

  // replaces timestamp
  lastIndexedTimestamp: z.string().optional(),

  // optional
  lastModifiedTimestamp: z.string().optional(),
  chunkId: z.string().optional(),
  description: z.string().optional(),
  domain: z.string().optional(),
  domainRef: compoundEntityRefSchema.optional(),
  domains: z.array(z.string()).optional(),
  lifecycle: z.string().optional(),
  owner: z.string().optional(),
  ownerRef: compoundEntityRefSchema.optional(),
  owners: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  summary: z.string().optional(),
  displayTitle: z.string().optional(),
  repo: z.string().optional(),
  repoSlug: z.string().optional(),
  custom: z.record(z.any()).optional(),
  score: z.number().optional(),

  // deprecated
  timestamp: z.string().optional(),
});

export const semanticSearchRetrieveAndGenerateCitationResultSchema = z.object({
  text: z.string().optional(),
  chunks: z.array(semanticSearchChunkSchema),
});
export type SemanticSearchRetrieveAndGenerateCitationResult = z.infer<
  typeof semanticSearchRetrieveAndGenerateCitationResultSchema
>;

export const semanticSearchWebSocketQuerySchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('message'),
    text: z.string(),
    sessionId: z.string().optional(),
    filters: semanticSearchFiltersSchema.optional(),
    agent: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('stop'),
    sessionId: z.string().optional(),
    agent: z.boolean().optional(),
  }),
]);
export type SemanticSearchWebSocketQuery = z.infer<
  typeof semanticSearchWebSocketQuerySchema
>;

export const semanticSearchWebSocketResponseSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal('rag-citation'),
      sessionId: z.string().optional(),
      result: semanticSearchRetrieveAndGenerateCitationResultSchema,
    }),
    z.object({
      type: z.literal('rag-message'),
      sessionId: z.string().optional(),
      result: z.object({
        text: z.string(),
      }),
    }),
    z.object({
      type: z.literal('rag-stop'),
      sessionId: z.string().optional(),
    }),
    z.object({
      type: z.literal('agent-message'),
      sessionId: z.string().optional(),
      result: z.object({
        text: z.string(),
      }),
    }),
    z.object({
      type: z.literal('agent-stop'),
      sessionId: z.string().optional(),
    }),
    z.object({
      type: z.literal('error'),
      message: z.string(),
    }),
  ],
);
export type SemanticSearchWebSocketResponse = z.infer<
  typeof semanticSearchWebSocketResponseSchema
>;

export const semanticSearchRetrieveApiResponseSchema = z.object({
  results: z.array(semanticSearchChunkSchema),
});
export type SemanticSearchRetrieveApiResponse = z.infer<
  typeof semanticSearchRetrieveApiResponseSchema
>;

export const semanticSearchRetrieveAndGenerateApiResponseSchema = z.object({
  results: z.array(semanticSearchRetrieveAndGenerateCitationResultSchema),
  sessionId: z.string().optional(),
});
export type SemanticSearchRetrieveAndGenerateApiResponse = z.infer<
  typeof semanticSearchRetrieveAndGenerateApiResponseSchema
>;

export const techdocsSearchIndexSchema = z.object({
  docs: z.array(
    z.object({
      title: z.string(),
      text: z.string(),
      location: z.string(),
    }),
  ),
});
export type TechDocsSearchIndex = z.infer<typeof techdocsSearchIndexSchema>;
